import config from "../config.js";
import { apiFetch } from "./apiClient.js";

const SERVICE_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const TEXT_FIELDS = ["name", "category", "problem", "description", "output", "access", "price", "action"];

function readService(row) {
  if (!row || typeof row.id !== "string" || row.id.length > 64 || !SERVICE_ID.test(row.id)
    || row.path !== `/lab/${row.id}` || row.status !== "published"
    || !Number.isFinite(Date.parse(row.publishedAt)) || !Number.isFinite(Date.parse(row.updatedAt))
    || TEXT_FIELDS.some((field) => typeof row[field] !== "string" || !row[field].trim())
    || !Array.isArray(row.keywords) || row.keywords.some((word) => typeof word !== "string")) {
    throw new Error("invalid lab catalog entry");
  }
  // Metadata cannot supply a Component or executable code. A document navigation
  // loads a newly deployed service even when this tab still has an older bundle.
  return Object.fromEntries([
    ...["id", "path", "status", "publishedAt", "updatedAt", "keywords", ...TEXT_FIELDS].map((key) => [key, row[key]]),
    ["reloadDocument", true],
  ]);
}

export async function fetchLabCatalog({ signal } = {}) {
  const services = [];
  const ids = new Set();
  const cursors = new Set();
  let after = "";
  do {
    if (cursors.has(after) || cursors.size >= 100) throw new Error("incomplete lab catalog pagination");
    cursors.add(after);
    const query = after ? `?after=${encodeURIComponent(after)}` : "";
    const response = await apiFetch(`${config.serverUri}/labs/services${query}`, {
      credentials: "include", headers: { Accept: "application/json" }, cache: "no-store", signal,
    });
    if (!response.ok) throw new Error(`lab catalog unavailable: ${response.status}`);
    const data = await response.json();
    if (!Array.isArray(data?.services) || typeof data.nextCursor !== "string"
      || (data.nextCursor && (data.nextCursor.length > 64 || !SERVICE_ID.test(data.nextCursor)))) {
      throw new Error("invalid lab catalog page");
    }
    for (const row of data.services) {
      const service = readService(row);
      if (ids.has(service.id)) throw new Error("duplicate lab catalog entry");
      ids.add(service.id);
      services.push(service);
    }
    if (data.nextCursor && data.nextCursor !== data.services.at(-1)?.id) throw new Error("invalid lab catalog cursor");
    after = data.nextCursor;
  } while (after);
  return services;
}
